// ANCHOR React
import React from 'react';

// ANCHOR Base
import { styled } from 'baseui';

// ANCHOR Theme
import { PRIMARY } from '@lyon/utils/theme/colors/primary';

// ANCHOR Utils
import { styletron } from '@lyon/utils/styletron';

// ANCHOR Hooks
import { useConstant } from '@lyon/utils/hooks/useConstant';

// ANCHOR Components
import { LyonPage } from '../LyonPage';

const DOT_DIAMETER = '8px';
const DOT_COUNT = 3;

const ANIMATION_DELAY = '0.2s';

interface ILoaderProps {
  isPageLoader?: boolean;
}

interface ILoadingDotProps {
  index: number;
  $isPageLoader?: boolean;
}

const LoadingAnimation = (isPageLoader?: boolean) => styletron.renderKeyframes({
  '0%': {
    transform: 'scale(1)',
  },
  '100%': {
    transform: `scale(${isPageLoader ? '2' : '1.5'})`,
  },
});

const LoadingDot = styled('div', ({ index, $isPageLoader }: ILoadingDotProps) => ({
  display: 'inline-block',
  width: DOT_DIAMETER,
  height: DOT_DIAMETER,
  borderRadius: '100%',
  marginLeft: $isPageLoader ? '16px' : '12px',
  marginRight: $isPageLoader ? '16px' : '12px',
  background: PRIMARY.primary50,

  animationName: LoadingAnimation($isPageLoader),
  animationDuration: `calc(${DOT_COUNT - 0.5} * ${ANIMATION_DELAY})`,
  animationDelay: `calc(${ANIMATION_DELAY} * ${index})`,
  animationTimingFunction: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
  animationIterationCount: 'infinite',
  animationDirection: 'alternate',
}));

const LoaderBase = styled('div', {
  width: '100%',
  height: '100%',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const LoaderContainer = styled('div', {
  marginTop: '64px',
  marginBottom: '64px',
});

export const Loader = React.memo(({ isPageLoader }: ILoaderProps) => {
  const children = useConstant(() => {
    const array = [];

    for (let i = 1; i <= DOT_COUNT; i += 1) {
      array.push(<LoadingDot
        key={i}
        index={i}
        $isPageLoader={isPageLoader}
      />);
    }

    return array;
  });

  if (isPageLoader) {
    return (
      <LyonPage hideSidebar>
        <LoaderBase>
          <LoaderContainer>
            { children }
          </LoaderContainer>
        </LoaderBase>
      </LyonPage>
    );
  }

  return (
    <LoaderBase>
      <LoaderContainer>
        { children }
      </LoaderContainer>
    </LoaderBase>
  );
});
